<script setup lang="ts">import { ref as _ref, computed as _computed } from 'vue';

import type GiftTemplate from '@consumer/types/GiftTemplate'
import type { GiftlyMetadata } from '@consumer/types/serializers'
import { ComboboxOption } from '@headlessui/vue'
import type { GIconDefinition } from '@consumer/components/GIcon.vue'
import type { Result } from './types'

withDefaults(defineProps<{
  text?: string
  hint?: string
  value?: Result
  icon?: GIconDefinition
  imageUrl?: string
  truncate?: boolean
  giftlyMetadata?: GiftlyMetadata
  withBorder?: boolean
}>(), {  })

let imgLoaded = _ref(!__props.imageUrl)

const gift = _computed(() => {
  if (!__props.giftlyMetadata) return undefined

  return {
    giftlyVersion: 3,
    giftlyMetadata: __props.giftlyMetadata,
    noTexts: true,
  } satisfies GiftTemplate
})

watchEffect(() => {
  imgLoaded.value = !__props.imageUrl
  if (__props.imageUrl) {
    const img = new Image()
    img.src = __props.imageUrl
    img.onload = () => {
      imgLoaded.value = true
    }
  }
})
</script>

<template>
  <ComboboxOption
    v-slot="{ active }"
    :value="value"
    as="div"
    class="search-result"
  >
    <div
      class="
        omni-search-result
        p-3 pr-5 rounded-v2md
        flex items-center gap-2
        group
        cursor-pointer
      "
      :class="{
        'border-1 border-grey-primary': withBorder,
        'bg-surface-blue': active
      }"
    >
      <div class="rounded-v2sm overflow-hidden" :style="{ width: '45px', height: '45px'}">
        <slot name="image">
          <div
            v-if="gift"
            class="icon-box bg-grey-100 w-full h-full"
          >
            <GiftlyImage :gift="gift" style="transform: rotate(-12deg); width: 35px;"/>
          </div>

          <div
            v-else-if="icon || imageUrl"
            class="flex items-center justify-center"
            :style="{ width: '45px', height: '45px'}"
          >
            <LoadingIndicator v-if="!imgLoaded" class="opacity-50 w-4 h-4"/>
            <div
              v-else
              class="icon-box g-icon w-full h-full"
              :class="{
                'bg-grey-100': !imageUrl && !active,
                'bg-white': !imageUrl && active,
              }"
              :style="{
                backgroundImage: imgLoaded && imageUrl ? `url(${imageUrl})` : 'none',
                backgroundSize: 'cover'
              }"
            >
              <div
                v-if="icon"
                class="flex items-center rounded-full"
              >
                <GIcon :icon="icon" size="20px"/>
              </div>
            </div>
          </div>
        </slot>
      </div>
      <div class="flex-grow flex flex-col">
        <div
          class="main-text font-semibold"
          :class="{ truncate }"
          :style="truncate && 'max-width: 300px'"
          v-html="text"
        />
        <div
          v-if="hint"
          :style="truncate && 'max-width: 200px'"
          class="text-xs text-v2-secondary"
          :class="{ truncate }"
        >
          {{ hint }}
        </div>
      </div>
      <slot name="addonRight"/>
    </div>
  </ComboboxOption>
</template>

<style scoped lang="scss">
  :deep(.giftly-image-v3) {
    border: none;
  }

  .icon-box {
    @apply flex items-center justify-center;
    --fa-primary-opacity: 1;
    --fa-secondary-opacity: 1;
    --fa-primary-color: var(--blue-900);
    --fa-secondary-color: var(--white-100);
  }

  .main-text {
    color: var(--blue-900);
  }
</style>
